// app.js
// Station — the browser client. Wires identity-browser.js,
// group-trust-browser.js, node-browser.js, group-chat.js, and
// file-share-browser.js to the DOM in src/browser/index.html.
//
// Scope: 1:1 (direct) chat, multi-party groups, and file sharing over
// either. group-chat.js's createGroup()/invite() were already generic
// (not direct-chat-specific) — this client's earlier version just
// never called them for more than two people. AI-peer awareness
// (settings.js/approval.js message types) is rendered here too:
// settings.js's own design makes trigger-mode changes visible group
// messages, not local config, and approval.js's own comment explicitly
// calls for a real approve/reject UI instead of asking a human to type
// JSON by hand — both honored directly.

import { loadOrCreateIdentity, fingerprintOf } from './identity-browser.js'
import { GroupTrustStore } from './group-trust-browser.js'
import { createBrowserNode } from './node-browser.js'
import { deriveNodePrivateKey } from './node-key.js'
import { ConnectionKeeper } from './connection-keeper.js'
import { StateVault } from './state-vault.js'
import * as grp from '../group.js'
import { GroupChatManager, directChatGroupId } from '../group-chat.js'
import { FileShareManager } from './file-share-browser.js'
import { parseSettingsMessage, formatSettingsMessage } from '../ai-peer/settings.js'
import { parseExecutionRequest, parseExecutionResponse, formatExecutionResponse } from '../ai-peer/approval.js'
import { peerIdFromString } from '@libp2p/peer-id'
import { multiaddr } from '@multiformats/multiaddr'
import { passthroughMapper } from '@libp2p/kad-dht'

// Exposed for local-loopback testing only (see
// tests/browser-client-e2e-test.mjs) — a tiny local test swarm (a
// handful of nodes total) can need every participant in DHT server mode
// for peer routing to succeed at all, the same accommodation
// tests/dht-discovery-test.mjs needed. Real deployments never touch
// this; it defaults to an empty override (no behavior change).
export const _testDhtOptions = { current: {} }

// A ready-made preset matching bootstrap-peer-cli.js's and cli.js's
// --local-testing flag — set _testDhtOptions.current to this (not a
// hand-rolled object) so all three components agree on the same DHT
// config; mismatched config across peers in a test swarm means they
// still won't find each other even with testing mode on for some of
// them. From the devtools console, before connecting:
//   const m = await import('./dist/app.bundle.js')
//   m._testDhtOptions.current = m.LOCAL_TESTING_DHT_OPTIONS
//   m._allowInsecureLocalTesting.current = true
export const LOCAL_TESTING_DHT_OPTIONS = { clientMode: false, peerInfoMapper: passthroughMapper }

// Local-testing TURN configuration - real fix for a real hairpin-NAT
// failure discovered during actual local testing (two browser tabs on
// the same machine, behind the same router, connecting via their
// STUN-discovered public address: candidates exchanged correctly,
// connectivity checks sent repeatedly, zero responses, eventual
// "failed" state - confirmed directly via chrome://webrtc-internals,
// not assumed). STUN alone can't help there; STUN only discovers your
// public address, it doesn't relay traffic when a router won't route a
// packet back to a different device on the same LAN. Requires running
// a local coturn instance - see README's Browser support section for
// setup. Matches this project's turnserver.conf exactly (same port,
// same test credentials) - a real deployment needs its own TURN
// server with real credentials, this is local-testing-only.
export const _testRtcConfiguration = { current: null }
export const LOCAL_TESTING_RTC_CONFIGURATION = {
  iceServers: [{ urls: 'turn:127.0.0.1:3478', username: 'testuser', credential: 'testpassword' }]
}

// Real deployments: Caddy proxies /turn-credentials to
// src/turn-credentials-service.js, which mints TURN credentials that expire
// (12h by default) instead of one password published in a static file.
// Fetched at connect time. Absent in local development (404), in which case
// the browser's default ICE servers apply, or the local-testing preset above
// when ?local-testing=1 is set. The config is fixed when the node is
// created, so WebRTC connections opened after the credential expires need a
// reconnect to pick up a fresh one.
async function loadDeployedRtcConfiguration () {
  try {
    const res = await fetch('./turn-credentials', { cache: 'no-store' })
    if (!res.ok) return null
    const config = await res.json()
    if (!Array.isArray(config.iceServers) || config.iceServers.length === 0) return null
    console.log('[station] using deployed TURN configuration')
    return config
  } catch {
    return null
  }
}

// Also local-testing-only, deliberately NOT a UI toggle (would be a real
// security footgun to expose visibly): libp2p's browser default refuses
// to dial private/loopback addresses and insecure ws:// — both correct
// production behavior. A real deployment's fix is serving the bootstrap
// peer over wss:// via a reverse proxy, not disabling this. Enable from
// the browser devtools console only, for testing against a loopback
// bootstrap peer before TLS is set up:
//   (await import('./dist/app.bundle.js'))._allowInsecureLocalTesting.current = true
export const _allowInsecureLocalTesting = { current: false }

const $ = (id) => document.getElementById(id)

// ---- DOM refs ----
const el = {
  dhtDot: $('dht-status-dot'),
  dhtText: $('dht-status-text'),
  ownFingerprint: $('own-fingerprint'),
  bootstrapInput: $('bootstrap-input'),
  bootstrapBtn: $('bootstrap-btn'),
  tabDirectBtn: $('tab-direct-btn'),
  tabGroupBtn: $('tab-group-btn'),
  newDirectPanel: $('new-direct-panel'),
  newGroupPanel: $('new-group-panel'),
  peerInput: $('peer-input'),
  connectBtn: $('connect-btn'),
  groupNameInput: $('group-name-input'),
  createGroupBtn: $('create-group-btn'),
  contactList: $('contact-list'),
  placeholder: $('placeholder'),
  conversation: $('conversation'),
  convPeerLabel: $('conv-peer-label'),
  convVerifiedBadge: $('conv-verified-badge'),
  membersToggleBtn: $('members-toggle-btn'),
  membersPanel: $('members-panel'),
  membersList: $('members-list'),
  triggerModeControl: $('trigger-mode-control'),
  triggerModeSelect: $('trigger-mode-select'),
  invitePeerInput: $('invite-peer-input'),
  invitePeerBtn: $('invite-peer-btn'),
  messages: $('messages'),
  messageInput: $('message-input'),
  sendBtn: $('send-btn'),
  attachBtn: $('attach-btn'),
  fileInput: $('file-input'),
  verifyOverlay: $('verify-overlay'),
  verifyPeerLabel: $('verify-peer-label'),
  verifyBlocks: $('verify-blocks'),
  verifyConfirmBtn: $('verify-confirm-btn'),
  verifyDismissBtn: $('verify-dismiss-btn'),
  identityOverlay: $('identity-overlay'),
  identityChoiceScreen: $('identity-choice-screen'),
  identityChoosePrfBtn: $('identity-choose-prf-btn'),
  identityChoosePassphraseBtn: $('identity-choose-passphrase-btn'),
  identityNewPassphraseScreen: $('identity-new-passphrase-screen'),
  identityNewPassphraseInput: $('identity-new-passphrase-input'),
  identityNewPassphraseConfirm: $('identity-new-passphrase-confirm'),
  identityNewPassphraseError: $('identity-new-passphrase-error'),
  identityNewPassphraseSubmitBtn: $('identity-new-passphrase-submit-btn'),
  identityUnlockScreen: $('identity-unlock-screen'),
  identityUnlockInput: $('identity-unlock-input'),
  identityUnlockError: $('identity-unlock-error'),
  identityUnlockSubmitBtn: $('identity-unlock-submit-btn'),
  ownAddressSection: $('own-address-section'),
  ownAddress: $('own-address'),
  copyAddressBtn: $('copy-address-btn'),
  refreshAddressBtn: $('refresh-address-btn')
}

function formatFingerprintHtml (fingerprint) {
  return fingerprint.split(' ').map((block) => `<span class="block">${block}</span>`).join(' ')
}

function setStatus (state, text) {
  el.dhtDot.className = `status-dot ${state}`
  el.dhtText.textContent = text
}

// ---- State ----
// Conversations are keyed by groupId (not peerId) — this is what lets
// the same data model cover both a 1:1 chat (a 2-member group with a
// deterministic ID, see group-chat.js's own directChatGroupId) and a
// real multi-party group without two separate code paths.
let identity, trustStore, mlsCtx, node, groupChat, keeper, vault
let latestMlsSnapshot = null // newest saved MLS group state (also what gets restored on the next connect)
let currentBootstrapAddr = null
let resumeTimer = null
let pendingVerification = null // { identityHex, fingerprint }
const conversations = new Map() // groupId -> ConversationState
let activeGroupId = null

// Diagnostics only: ?debug exposes the live node and group state on
// window.__station so connection problems can be inspected from the
// devtools console (node.getConnections(), groupChat.groups, ...). Off by
// default — nothing is exposed unless the URL asks for it.
if (new URLSearchParams(globalThis.location?.search ?? '').has('debug')) {
  window.__station = {
    get node () { return node },
    get groupChat () { return groupChat },
    get conversations () { return conversations },
    get vault () { return vault }
  }
}

/**
 * @typedef {object} ConversationState
 * @property {string} groupId
 * @property {'direct'|'group'} type
 * @property {import('@libp2p/interface').PeerId} [peerId] - direct chats only
 * @property {string} [name] - group chats only, user-chosen label
 * @property {Array} messages
 * @property {boolean} verified - direct chats only (fingerprint confirmed)
 * @property {FileShareManager} fileShare
 * @property {Set<string>} aiMemberPeerIdStrs - peers observed sending an
 *   ai-peer-exec-request message — the one reliable signal that a member
 *   is an actual running AI peer rather than a human (a human wouldn't
 *   normally send one manually). Settings messages aren't used for this:
 *   any human can legitimately send those too, by settings.js's own design.
 * @property {string|null} triggerMode - last-seen AI response mode for
 *   this group, or null if never set
 */

// ---- Identity protection setup/unlock UI ----
// Each function shows the relevant screen within #identity-overlay and
// resolves with what the user provided — this is exactly the ui object
// shape EncryptedIdentityStorage expects (see its own doc comment).
function showIdentityScreen (screen) {
  el.identityOverlay.classList.add('open')
  for (const s of [el.identityChoiceScreen, el.identityNewPassphraseScreen, el.identityUnlockScreen]) {
    s.style.display = s === screen ? 'block' : 'none'
  }
}

function hideIdentityOverlay () {
  el.identityOverlay.classList.remove('open')
}

const identityUi = {
  choosePrfOrPassphrase () {
    return new Promise((resolve) => {
      showIdentityScreen(el.identityChoiceScreen)
      el.identityChoosePrfBtn.onclick = () => { hideIdentityOverlay(); resolve('prf') }
      el.identityChoosePassphraseBtn.onclick = () => { hideIdentityOverlay(); resolve('passphrase') }
    })
  },

  promptNewPassphrase () {
    return new Promise((resolve) => {
      showIdentityScreen(el.identityNewPassphraseScreen)
      el.identityNewPassphraseInput.value = ''
      el.identityNewPassphraseConfirm.value = ''
      el.identityNewPassphraseError.textContent = ''
      el.identityNewPassphraseSubmitBtn.onclick = () => {
        const pw = el.identityNewPassphraseInput.value
        const confirm = el.identityNewPassphraseConfirm.value
        if (pw.length < 8) {
          el.identityNewPassphraseError.textContent = 'At least 8 characters.'
          return
        }
        if (pw !== confirm) {
          el.identityNewPassphraseError.textContent = 'Passphrases do not match.'
          return
        }
        hideIdentityOverlay()
        resolve(pw)
      }
    })
  },

  promptExistingPassphrase () {
    return new Promise((resolve) => {
      showIdentityScreen(el.identityUnlockScreen)
      el.identityUnlockInput.value = ''
      el.identityUnlockError.textContent = ''
      el.identityUnlockSubmitBtn.onclick = () => {
        const pw = el.identityUnlockInput.value
        hideIdentityOverlay()
        resolve(pw)
      }
    })
  },

  promptRetryPassphrase (err) {
    return new Promise((resolve) => {
      showIdentityScreen(el.identityUnlockScreen)
      el.identityUnlockInput.value = ''
      el.identityUnlockError.textContent = err.message
      el.identityUnlockSubmitBtn.onclick = () => {
        const pw = el.identityUnlockInput.value
        hideIdentityOverlay()
        resolve(pw)
      }
    })
  }
}

// ---- Boot: identity + trust store don't need any network ----
async function boot () {
  // URL-parameter-based local testing mode, replacing the console-based
  // approach — that required running the right commands in the right
  // order every time, and got wiped on every refresh, both of which
  // proved unreliable in practice. ?local-testing=1 is visible in the
  // URL bar (not hidden), requires deliberate action to add (same threat
  // model as the console approach), and survives refreshes automatically
  // since it's just part of the URL. Never set this in a real deployment
  // URL you'd share with anyone.
  if (new URLSearchParams(globalThis.location?.search ?? "").has("local-testing")) {
    _testDhtOptions.current = LOCAL_TESTING_DHT_OPTIONS
    _allowInsecureLocalTesting.current = true
    _testRtcConfiguration.current = LOCAL_TESTING_RTC_CONFIGURATION
    document.title = "WARNING LOCAL TESTING MODE - " + document.title
  }

  identity = await loadOrCreateIdentity(identityUi)
  el.ownFingerprint.innerHTML = formatFingerprintHtml(identity.fingerprint)

  trustStore = new GroupTrustStore()
  await trustStore.load()

  mlsCtx = await grp.makeMlsContext(trustStore, (identityHex, fingerprint) => {
    // Fires the first time we see a given identity, regardless of which
    // conversation surfaces it first — queue it for the verification
    // overlay. If a conversation is already open with this identity's
    // peer, this also updates that conversation's badge once confirmed.
    pendingVerification = { identityHex, fingerprint }
    showVerifyOverlay(fingerprint)
  })

  // Only now is it safe to connect — GroupChatManager's constructor
  // needs a real identity/mlsCtx, and boot() may have just spent a
  // while waiting on a passphrase/biometric prompt to unlock an
  // existing identity. The button starts disabled in the HTML
  // specifically so a click during that wait can't slip through.
  // Everything below depends on the identity being unlocked: the vault's key
  // is derived from it.
  vault = new StateVault(identity)
  latestMlsSnapshot = await vault.load('mls-state')
  restoreConversations(await vault.load('conversations'))
  if (vault.unreadable.size > 0) {
    console.warn('[station] saved state could not be read (different identity or damaged); starting empty:', [...vault.unreadable.keys()].join(', '))
  }

  el.bootstrapBtn.disabled = false
  setStatus('', 'not connected — provide a bootstrap peer')

  // Reconnect on our own if we've connected before: after a reload the only
  // thing standing between a restored conversation and a working one would
  // otherwise be pasting the same address again.
  const remembered = rememberedBootstrap()
  if (remembered) {
    el.bootstrapInput.value = remembered
    el.bootstrapBtn.click()
  }
}

function showVerifyOverlay (fingerprint) {
  el.verifyPeerLabel.textContent = 'New contact'
  el.verifyBlocks.innerHTML = fingerprint.split(' ').map((block, i) =>
    `<span style="animation-delay:${i * 40}ms">${block}</span>`).join('')
  el.verifyOverlay.classList.add('open')
}

el.verifyConfirmBtn.onclick = () => {
  el.verifyOverlay.classList.remove('open')
  const conv = conversations.get(activeGroupId)
  if (conv && conv.type === 'direct') { conv.verified = true; renderConversationHeader(conv); saveConversationsSoon() }
  pendingVerification = null
}
el.verifyDismissBtn.onclick = () => {
  el.verifyOverlay.classList.remove('open')
  pendingVerification = null
}

// ---- Bootstrap connection ----
// The connection keeper reports the real state of the link to the bootstrap
// peer (which also carries our relay reservation), not just that we once
// connected.
function showKeeperState (state, detail) {
  if (state === 'connecting') {
    setStatus('pending', 'connecting…')
  } else if (state === 'connected') {
    setStatus('live', `connected — ${node.peerId.toString().slice(-8)}`)
    groupChat?.resumeGroups().catch(() => {})
  } else if (state === 'reconnecting') {
    console.warn('[station] connection to the bootstrap peer lost:', detail)
    setStatus('pending', 'connection lost — reconnecting…')
  }
}

el.bootstrapBtn.onclick = async () => {
  const addr = el.bootstrapInput.value.trim()
  if (!addr) return

  // Already running against this bootstrap peer: just redial it on the same
  // node. Rebuilding would throw away the groups and open connections.
  if (node && keeper && keeper.bootstrapAddr === addr) {
    setStatus('pending', 'reconnecting…')
    await keeper.reconnectNow()
    return
  }

  el.bootstrapBtn.disabled = true
  setStatus('pending', 'connecting…')
  try {
    // A different bootstrap peer (or first connect) builds a new node. Stop
    // the old one first, or it keeps running in the background, still holding
    // a relay reservation.
    if (keeper) { keeper.stop(); keeper = null }
    if (node) {
      await node.stop().catch(() => {})
      node = null
    }
    node = await createBrowserNode({
      bootstrapPeers: [addr],
      dhtOptions: _testDhtOptions.current,
      allowInsecureLocalTesting: _allowInsecureLocalTesting.current,
      rtcConfiguration: _testRtcConfiguration.current ?? await loadDeployedRtcConfiguration(),
      // Same identity -> same peer ID across reloads and reconnects, so
      // addresses people already have keep working.
      privateKey: await deriveNodePrivateKey(identity)
    })
    currentBootstrapAddr = addr
    groupChat = new GroupChatManager(node, identity, mlsCtx, {
      stateStore: { save: saveMlsSnapshot },
      addressesFor: addressesForPeer
    })
    wireGroupChatEvents()
    if (latestMlsSnapshot) {
      try {
        groupChat.importState(latestMlsSnapshot)
      } catch (err) {
        console.error('[station] could not restore saved groups:', err.message)
      }
    }
    for (const conv of conversations.values()) if (!conv.fileShare) attachFileShare(conv)
    keeper = new ConnectionKeeper({ node, bootstrapAddr: addr, onState: showKeeperState })
    keeper.start() // drives the status line from here on
    startResumeLoop()
    rememberBootstrap(addr)
    el.connectBtn.disabled = false
    el.createGroupBtn.disabled = false
    node.addEventListener('self:peer:update', refreshOwnAddressDisplay)
    refreshOwnAddressDisplay()
    el.bootstrapInput.closest('.network-panel')?.removeAttribute('open')
  } catch (err) {
    console.error('[station] bootstrap connect failed:', err.stack || err.message)
    setStatus('', `connection failed: ${err.message}`)
  } finally {
    el.bootstrapBtn.disabled = false
  }
}

// The WebRTC relay reservation ('/webrtc' listen address) takes a
// moment to complete after the bootstrap connection itself, with real
// timing variance depending on network conditions — rather than a
// short fixed-timeout poll (which proved too impatient in practice),
// listen for libp2p's own 'self:peer:update' event, which fires
// whenever this node's own known addresses change, AND keep a manual
// refresh available as a fallback in case that event doesn't fire for
// some reason.
function refreshOwnAddressDisplay () {
  if (!node) return
  const addrs = node.getMultiaddrs().map((a) => a.toString())
  const webrtcAddrs = addrs.filter((a) => a.includes('/webrtc'))
  // A machine can have multiple network interfaces (LAN, VPN, etc.) —
  // the relay hop embedded in a webrtc address might route through one
  // that isn't actually reachable, even though it's a valid-looking
  // enumerated interface. Prefer loopback (127.0.0.1) for local
  // testing, same logic already used for the DHT-lookup dial path.
  const webrtcAddr = webrtcAddrs.find((a) => a.includes('127.0.0.1')) ?? webrtcAddrs[0]
  el.ownAddress.textContent = webrtcAddr ?? addrs[0] ?? '(no address yet — try Refresh in a moment)'
  el.ownAddressSection.style.display = 'block'
}

el.refreshAddressBtn.onclick = refreshOwnAddressDisplay

el.copyAddressBtn.onclick = () => {
  navigator.clipboard?.writeText(el.ownAddress.textContent)
  el.copyAddressBtn.textContent = 'Copied'
  setTimeout(() => { el.copyAddressBtn.textContent = 'Copy' }, 1500)
}

// The bootstrap address is public (it's a server everyone uses), so it's kept in
// plain localStorage rather than the encrypted vault, and used to reconnect
// automatically once the identity is unlocked.
const BOOTSTRAP_KEY = 'station.bootstrap'
function rememberBootstrap (addr) {
  try { globalThis.localStorage?.setItem(BOOTSTRAP_KEY, addr) } catch { /* storage unavailable: just won't auto-connect */ }
}
function rememberedBootstrap () {
  try { return globalThis.localStorage?.getItem(BOOTSTRAP_KEY) ?? null } catch { return null }
}

function attachFileShare (conv) {
  conv.fileShare = new FileShareManager(
    (msg) => groupChat.send(conv.groupId, msg),
    (result) => onFileReceived(conv, result)
  )
}

// ---- Surviving a reload ----
// Two encrypted records (see state-vault.js): 'mls-state', the group
// cryptography, saved by GroupChatManager itself before anything is sent; and
// 'conversations', what the screen shows. The conversation record is saved a
// moment after changes (and when the tab is hidden), since it only affects what
// you see, not whether messages can be read.
const MAX_SAVED_MESSAGES = 500

function serializeConversations () {
  return {
    version: 1,
    conversations: [...conversations.values()].map((conv) => ({
      groupId: conv.groupId,
      type: conv.type,
      peerIdStr: conv.peerId?.toString() ?? null,
      name: conv.name ?? null,
      verified: !!conv.verified,
      aiMembers: [...conv.aiMemberPeerIdStrs],
      triggerMode: conv.triggerMode ?? null,
      messages: conv.messages.map((m) => {
        // Plain text and answered approval cards survive. File links hold
        // blob: URLs that die with the page, and an unanswered approval card
        // would offer to approve a request the AI is no longer waiting on.
        if (m.execCard) return m.execCard.result ? { execCard: m.execCard, at: m.at } : null
        if (typeof m.text === 'string' && !m.html) return { who: m.who, text: m.text, at: m.at }
        return null
      }).filter(Boolean).slice(-MAX_SAVED_MESSAGES)
    }))
  }
}

function restoreConversations (saved) {
  if (saved?.version !== 1) return
  for (const c of saved.conversations) {
    if (conversations.has(c.groupId)) continue
    conversations.set(c.groupId, {
      groupId: c.groupId,
      type: c.type,
      peerId: c.peerIdStr ? peerIdFromString(c.peerIdStr) : undefined,
      name: c.name ?? undefined,
      messages: c.messages,
      verified: c.verified,
      fileShare: null, // needs a live GroupChatManager; attached on connect
      aiMemberPeerIdStrs: new Set(c.aiMembers),
      triggerMode: c.triggerMode
    })
  }
  renderContactList()
}

let saveTimer = null
async function saveConversationsNow () {
  clearTimeout(saveTimer)
  saveTimer = null
  if (!vault) return
  try {
    await vault.save('conversations', serializeConversations())
  } catch (err) {
    console.error('[station] could not save conversations:', err.message)
  }
}
function saveConversationsSoon () {
  if (!vault || saveTimer) return
  saveTimer = setTimeout(saveConversationsNow, 400)
}
globalThis.addEventListener?.('pagehide', saveConversationsNow)
globalThis.document?.addEventListener?.('visibilitychange', () => { if (globalThis.document.hidden) saveConversationsNow() })

async function saveMlsSnapshot (snapshot) {
  latestMlsSnapshot = snapshot
  await vault.save('mls-state', snapshot)
}

// Peers we've talked to are reachable by a deterministic address: the relay on
// our bootstrap peer, then a WebRTC hop to their (stable) peer ID.
function addressesForPeer (peerIdStr) {
  return currentBootstrapAddr ? [`${currentBootstrapAddr}/p2p-circuit/webrtc/p2p/${peerIdStr}`] : []
}

// Re-attach to anyone we have a conversation with but no live stream to:
// after a reload, or after a connection died. Peers that are offline just fail
// quietly and are retried.
function startResumeLoop () {
  clearInterval(resumeTimer)
  const tick = () => groupChat?.resumeGroups().catch(() => {})
  tick()
  resumeTimer = setInterval(tick, 30000)
}

// Any chat this browser takes part in needs a conversation entry, however
// it came about: we started it, we were invited into it, or we founded it
// because the other side asked (the lower peer ID of a direct pair always
// founds it, so the peer who clicked "Open" is often NOT the founder).
// Only the first of those goes through the Open/Create handlers, so every
// other path funnels through here. Without it the group exists in the
// encrypted layer but nothing shows it, and the 'message' handler drops
// everything for a group it has no conversation for.
function ensureConversation (groupId) {
  const existing = conversations.get(groupId)
  if (existing) return existing
  if (!groupChat?.groups.has(groupId)) return null
  const conv = {
    groupId, messages: [], verified: false,
    aiMemberPeerIdStrs: new Set(), triggerMode: null
  }
  if (groupId.startsWith('direct:')) {
    const ownId = node.peerId.toString()
    const otherId = groupId.split(':').slice(1).find((id) => id !== ownId)
    conv.type = 'direct'
    conv.peerId = peerIdFromString(otherId)
  } else {
    conv.type = 'group'
    conv.name = `Group ${groupId.slice(-6)}`
  }
  attachFileShare(conv)
  conversations.set(groupId, conv)
  renderContactList()
  return conv
}

function wireGroupChatEvents () {
  groupChat.on('joined', (groupId) => ensureConversation(groupId))
  groupChat.on('restored', (groupId) => ensureConversation(groupId))
  groupChat.on('undelivered', (groupId, peerIdStr) => {
    const conv = conversations.get(groupId)
    if (conv) appendSystemMessage(conv, `Not delivered to ${peerIdStr.slice(-8)} — they aren't connected right now. It won't be sent later.`)
  })
  groupChat.on('member-added', (groupId) => ensureConversation(groupId))

  groupChat.on('message', async (groupId, senderPeerIdStr, text) => {
    const conv = ensureConversation(groupId)
    if (!conv) return

    // File-share protocol messages (file-offer/file-chunk) are consumed
    // here before ever reaching the chat display.
    const wasFileMessage = conv.fileShare && await conv.fileShare.handleIncomingMessage(text, senderPeerIdStr)
    if (wasFileMessage) return

    // AI-peer message types — settings.js/approval.js's own JSON-tagged
    // convention, layered on top of ordinary group messages. Render
    // each distinctly rather than as plain chat text.
    const settingsMsg = parseSettingsMessage(text)
    if (settingsMsg) {
      conv.triggerMode = settingsMsg.triggerMode
      appendSystemMessage(conv, `AI response mode set to "${settingsMsg.triggerMode}"${settingsMsg.setBy ? ` by ${settingsMsg.setBy.slice(0, 9)}` : ''}`)
      if (groupId === activeGroupId) syncTriggerModeSelect(conv)
      return
    }

    const execReq = parseExecutionRequest(text)
    if (execReq) {
      // The one reliable signal that this peer is an actual running AI
      // peer, not a human — a human wouldn't normally send one of these
      // manually. Settings messages aren't used for this detection;
      // any group member can legitimately send those too.
      conv.aiMemberPeerIdStrs.add(senderPeerIdStr)
      appendExecRequestCard(conv, execReq, senderPeerIdStr)
      if (groupId === activeGroupId) renderMembers(conv)
      return
    }

    const execRes = parseExecutionResponse(text)
    if (execRes) {
      appendSystemMessage(conv, `Execution ${execRes.approved ? 'approved' : 'rejected'}${execRes.by ? ` by ${execRes.by.slice(0, 9)}` : ''}`)
      resolveExecCard(conv, execRes)
      return
    }

    const isAiSender = conv.aiMemberPeerIdStrs.has(senderPeerIdStr)
    appendMessage(conv, isAiSender ? 'ai' : 'theirs', text)
  })
  groupChat.on('error', (err, peerIdStr) => {
    console.error('[station]', peerIdStr, err.message)
  })
}

// ---- Resolving a peer-ID-or-address input, shared by "Open" (direct
// chat) and "Invite" (group) — both need the exact same dial logic. ----
async function resolveAndDialPeer (input) {
  let peerId, dialAddr, isWebrtcDial = false

  if (input.startsWith('/')) {
    // A full multiaddr, not just a peer ID — needed for peers only
    // reachable via circuit relay/WebRTC (any other browser peer).
    // DHT-based discovery doesn't reliably work for such peers — this
    // isn't a bug here, it's documented libp2p behavior (the WebRTC
    // guide notes "browser peers don't tend to be long-lived enough to
    // appear in [DHT] results" — confirmed directly against a real
    // WebRTC connection during development, see tests/browser-webrtc-
    // test.mjs). The real fix is GossipSub-based address publishing
    // (what libp2p's own guide recommends for this exact case) — out of
    // scope for now; pasting the full address is the practical
    // workaround, and the WebRTC transport itself is proven to work
    // once you have it.
    const ma = multiaddr(input)
    const peerIdStr = ma.getComponents().findLast((c) => c.name === 'p2p')?.value
    if (!peerIdStr) throw new Error('This address has no peer ID in it — needs a /p2p/<id> segment at the end.')
    peerId = peerIdFromString(peerIdStr)
    dialAddr = ma
    isWebrtcDial = true
  } else {
    peerId = peerIdFromString(input)
    // A bare PeerId has no known address yet — look it up via the DHT
    // (see tests/dht-discovery-test.mjs, which proved this exact
    // lookup-then-dial sequence works for Node peers specifically —
    // Node peers have real dialable addresses the DHT can return, this
    // path isn't affected by the browser-peer DHT limitation above).
    setStatus('pending', `locating ${input.slice(-8)}…`)
    const peerInfo = await node.peerRouting.findPeer(peerId)
    const wsAddrs = peerInfo?.multiaddrs?.filter((ma) => ma.toString().includes('/ws')) ?? []
    dialAddr = wsAddrs.find((ma) => ma.toString().includes('127.0.0.1')) ?? wsAddrs[0]
  }

  if (dialAddr) {
    // libp2p's default per-address dial timeout (6s) is tuned for a
    // simple direct connection — a real WebRTC connection has to signal
    // through the relay, exchange SDP, and complete real ICE
    // negotiation (real STUN round-trips, not instant on a genuine
    // browser network stack) before it's actually connected, which can
    // easily take longer.
    await node.dial(dialAddr, isWebrtcDial ? { signal: AbortSignal.timeout(30000) } : {})
  }
  setStatus('live', `connected — ${node.peerId.toString().slice(-8)}`)
  return peerId
}

// ---- New transmission: direct vs group tab switch ----
el.tabDirectBtn.onclick = () => {
  el.tabDirectBtn.classList.add('active')
  el.tabGroupBtn.classList.remove('active')
  el.newDirectPanel.style.display = 'block'
  el.newGroupPanel.style.display = 'none'
}
el.tabGroupBtn.onclick = () => {
  el.tabGroupBtn.classList.add('active')
  el.tabDirectBtn.classList.remove('active')
  el.newGroupPanel.style.display = 'block'
  el.newDirectPanel.style.display = 'none'
}

// ---- Starting a direct chat ----
el.connectBtn.onclick = async () => {
  const input = el.peerInput.value.trim()
  if (!input || !groupChat) return
  el.connectBtn.disabled = true
  try {
    const peerId = await resolveAndDialPeer(input)
    const peerIdStr = peerId.toString()
    const groupId = directChatGroupId(node.peerId.toString(), peerIdStr)

    if (!conversations.has(groupId)) {
      const conv = {
        groupId, type: 'direct', peerId, messages: [], verified: false,
        aiMemberPeerIdStrs: new Set(), triggerMode: null
      }
      attachFileShare(conv)
      conversations.set(groupId, conv)
      renderContactList()
    }
    await groupChat.startDirectChat(peerId)
    openConversation(groupId)
    el.peerInput.value = ''
  } catch (err) {
    console.error('[station] connect failed:', err.stack || err.message)
    alert(`Could not start chat: ${err.message}`)
  } finally {
    el.connectBtn.disabled = false
  }
}

// ---- Creating a new group ----
el.createGroupBtn.onclick = async () => {
  const name = el.groupNameInput.value.trim()
  if (!name || !groupChat) return
  el.createGroupBtn.disabled = true
  try {
    const groupId = `group:${crypto.randomUUID()}`
    await groupChat.createGroup(groupId)
    const conv = {
      groupId, type: 'group', name, messages: [], verified: false,
      aiMemberPeerIdStrs: new Set(), triggerMode: null
    }
    attachFileShare(conv)
    conversations.set(groupId, conv)
    renderContactList()
    openConversation(groupId)
    el.groupNameInput.value = ''
  } catch (err) {
    console.error('[station] create group failed:', err.stack || err.message)
    alert(`Could not create group: ${err.message}`)
  } finally {
    el.createGroupBtn.disabled = false
  }
}

// ---- Inviting a member into the currently-open group ----
el.invitePeerBtn.onclick = async () => {
  const input = el.invitePeerInput.value.trim()
  const conv = conversations.get(activeGroupId)
  if (!input || !conv || conv.type !== 'group') return
  el.invitePeerBtn.disabled = true
  try {
    const peerId = await resolveAndDialPeer(input)
    await groupChat.invite(conv.groupId, peerId)
    appendSystemMessage(conv, `Invited ${peerId.toString().slice(-8)}`)
    renderMembers(conv)
    el.invitePeerInput.value = ''
  } catch (err) {
    console.error('[station] invite failed:', err.stack || err.message)
    alert(`Could not invite that peer: ${err.message}`)
  } finally {
    el.invitePeerBtn.disabled = false
  }
}

// ---- Conversation UI ----
function renderContactList () {
  saveConversationsSoon()
  if (conversations.size === 0) {
    el.contactList.innerHTML = '<div class="empty-state">No conversations yet. Open one above once you\'re connected to a bootstrap peer.</div>'
    return
  }
  el.contactList.innerHTML = ''
  for (const [groupId, conv] of conversations) {
    const div = document.createElement('div')
    div.className = 'contact' + (groupId === activeGroupId ? ' active' : '')
    const last = conv.messages.at(-1)
    const label = conv.type === 'group' ? escapeHtml(conv.name) : conv.peerId.toString().slice(-12)
    const icon = conv.type === 'group' ? '◈' : '◆'
    div.innerHTML = `<span class="contact-icon">${icon}</span><div><div class="contact-name">${label}</div><div class="contact-preview">${last ? escapeHtml(last.text ?? '') : 'No messages yet'}</div></div>`
    div.onclick = () => openConversation(groupId)
    el.contactList.appendChild(div)
  }
}

function openConversation (groupId) {
  activeGroupId = groupId
  el.placeholder.style.display = 'none'
  el.conversation.style.display = 'flex'
  const conv = conversations.get(groupId)
  renderConversationHeader(conv)
  renderMessages(conv)
  renderContactList()
  el.membersPanel.style.display = 'none'
}

function renderConversationHeader (conv) {
  el.convPeerLabel.textContent = conv.type === 'group' ? conv.name : conv.peerId.toString()
  el.convVerifiedBadge.style.display = conv.type === 'direct' && conv.verified ? 'inline-block' : 'none'
  el.membersToggleBtn.style.display = 'inline-block'
  el.membersToggleBtn.textContent = conv.type === 'group' ? `Members (${1 + (groupChat?.groups.get(conv.groupId)?.members.size ?? 0)})` : 'Members'
  if (conv.type === 'group') renderMembers(conv)
}

el.membersToggleBtn.onclick = () => {
  el.membersPanel.style.display = el.membersPanel.style.display === 'none' ? 'block' : 'none'
}

// ---- Members panel — the station roster. Every participant shown as a
// labeled station with a live signal; AI members get a visibly
// different pulse (not just a different color, see .station-pulse.synth
// in index.html), so the distinction holds even without color. ----
function renderMembers (conv) {
  const group = groupChat?.groups.get(conv.groupId)
  el.membersList.innerHTML = ''

  const selfRow = document.createElement('div')
  selfRow.className = 'member-row'
  selfRow.innerHTML = `<span class="station-pulse"></span><div class="member-info"><div class="member-callsign">${formatCallsign(identity.fingerprint)}</div><div class="member-role">you</div></div>`
  el.membersList.appendChild(selfRow)

  for (const [peerIdStr, member] of group?.members ?? []) {
    const isAi = conv.aiMemberPeerIdStrs.has(peerIdStr)
    const callsign = member.identityHex ? formatCallsign(hexToFingerprint(member.identityHex)) : peerIdStr.slice(-12)
    const row = document.createElement('div')
    row.className = 'member-row'
    row.innerHTML = `<span class="station-pulse${isAi ? ' synth' : ''}"></span><div class="member-info"><div class="member-callsign">${callsign}</div><div class="member-role${isAi ? ' synth' : ''}">${isAi ? 'automated station' : 'operator'}</div></div>`
    el.membersList.appendChild(row)
  }

  // The AI response-mode control lives here regardless of whether an
  // AI member has actually joined yet — a human may reasonably want to
  // set it in advance of inviting one, and the group should agree on it
  // together either way (settings.js's own design: this is a visible
  // group message, not private local config).
  if (conv.type === 'group') {
    el.triggerModeControl.style.display = 'block'
    syncTriggerModeSelect(conv)
  } else {
    el.triggerModeControl.style.display = 'none'
  }

  el.membersToggleBtn.textContent = `Members (${1 + (group?.members.size ?? 0)})`
}

function syncTriggerModeSelect (conv) {
  if (conv.triggerMode) el.triggerModeSelect.value = conv.triggerMode
}

el.triggerModeSelect.onchange = async () => {
  const conv = conversations.get(activeGroupId)
  if (!conv || conv.type !== 'group') return
  const mode = el.triggerModeSelect.value
  await groupChat.send(conv.groupId, formatSettingsMessage(mode, identity.fingerprint))
  conv.triggerMode = mode
  appendSystemMessage(conv, `AI response mode set to "${mode}" by you`)
}

function formatCallsign (fingerprint) {
  return fingerprint.split(' ').slice(0, 2).join(' ')
}

function hexToFingerprint (hex) {
  return hex.match(/.{1,4}/g)?.join(' ') ?? hex
}

function renderMessages (conv) {
  el.messages.innerHTML = ''
  for (const m of conv.messages) appendMessageEl(m)
}

function appendMessage (conv, who, text) {
  const m = { who, text, at: Date.now() }
  conv.messages.push(m)
  if (conv.groupId === activeGroupId) appendMessageEl(m)
  renderContactList()
}

function appendSystemMessage (conv, text) {
  appendMessage(conv, 'system', text)
}

function appendMessageEl (m) {
  const div = document.createElement('div')
  if (m.execCard) {
    div.className = 'exec-card'
    div.dataset.execId = m.execCard.id
    div.innerHTML = renderExecCardHtml(m.execCard)
    wireExecCardButtons(div, m)
  } else {
    div.className = `msg ${m.who}`
    div.innerHTML = m.html ?? escapeHtml(m.text)
  }
  el.messages.appendChild(div)
  el.messages.scrollTop = el.messages.scrollHeight
}

// ---- AI execution-approval cards — a real approve/reject control,
// answering approval.js's own comment directly: "A real UI would render
// the request as an actual approve/reject button pair that emits the
// response message automatically, rather than asking a human to type
// JSON by hand." ----
function renderExecCardHtml (execCard) {
  const resultLine = execCard.result
    ? `<div class="exec-result">${execCard.result}</div>`
    : ''
  const actions = execCard.result
    ? ''
    : `<div class="exec-actions"><button data-exec-action="approve">Approve</button><button class="secondary" data-exec-action="reject">Reject</button></div>`
  return `<p class="exec-eyebrow">◈ Execution request — ${escapeHtml(execCard.language)}</p><pre>${escapeHtml(execCard.code)}</pre>${actions}${resultLine}`
}

function wireExecCardButtons (div, m) {
  const approveBtn = div.querySelector('[data-exec-action="approve"]')
  const rejectBtn = div.querySelector('[data-exec-action="reject"]')
  if (!approveBtn) return
  const respond = async (approved) => {
    const conv = conversations.get(activeGroupId)
    if (!conv) return
    approveBtn.disabled = true
    rejectBtn.disabled = true
    await groupChat.send(conv.groupId, formatExecutionResponse(m.execCard.id, approved, identity.fingerprint))
    m.execCard.result = `You ${approved ? 'approved' : 'rejected'} this — waiting for it to take effect.`
    div.innerHTML = renderExecCardHtml(m.execCard)
  }
  approveBtn.onclick = () => respond(true)
  rejectBtn.onclick = () => respond(false)
}

function appendExecRequestCard (conv, execReq, senderPeerIdStr) {
  const m = { execCard: { id: execReq.id, language: execReq.language, code: execReq.code, result: null }, at: Date.now() }
  conv.messages.push(m)
  if (conv.groupId === activeGroupId) appendMessageEl(m)
  renderContactList()
}

function resolveExecCard (conv, execRes) {
  const m = conv.messages.find((msg) => msg.execCard?.id === execRes.requestId)
  if (!m) return
  m.execCard.result = `${execRes.approved ? 'Approved' : 'Rejected'}${execRes.by ? ` by ${execRes.by.slice(0, 9)}` : ''}.`
  saveConversationsSoon()
  if (conv.groupId === activeGroupId) renderMessages(conv)
}

function escapeHtml (s) {
  const div = document.createElement('div')
  div.textContent = s
  return div.innerHTML
}

// ---- Sending ----
async function sendCurrentMessage () {
  const text = el.messageInput.value.trim()
  if (!text || !activeGroupId) return
  const conv = conversations.get(activeGroupId)
  if (!groupChat) {
    appendSystemMessage(conv, 'Message not sent: not connected yet — connect to a bootstrap peer first.')
    return
  }
  try {
    await groupChat.send(conv.groupId, text)
  } catch (err) {
    console.error('[station] send failed:', err.stack || err.message)
    appendSystemMessage(conv, `Message not sent: ${err.message}`)
    return
  }
  appendMessage(conv, 'mine', text)
  el.messageInput.value = ''
}
el.sendBtn.onclick = sendCurrentMessage
el.messageInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    sendCurrentMessage()
  }
})

// ---- File sharing ----
el.attachBtn.onclick = () => el.fileInput.click()
el.fileInput.onchange = async () => {
  const file = el.fileInput.files[0]
  if (!file || !activeGroupId) return
  const conv = conversations.get(activeGroupId)
  const result = await conv.fileShare.offerBrowserFile(file)
  conv.messages.push({ who: 'mine', html: `<span class="file-link">📎 ${escapeHtml(file.name)} (sending…)</span>`, at: Date.now() })
  renderMessages(conv)
  el.fileInput.value = ''
}

function onFileReceived (conv, result) {
  if (result.error) {
    conv.messages.push({ who: 'system', text: `File transfer failed: ${result.error}` })
  } else {
    conv.messages.push({
      who: 'theirs',
      html: `<a class="file-link" href="${result.blobUrl}" download="${escapeHtml(result.filename)}">📎 ${escapeHtml(result.filename)} — download</a>`
    })
  }
  if (conv.groupId === activeGroupId) renderMessages(conv)
  renderContactList()
}

boot().catch((err) => {
  // A genuine safety net, not expected to fire in normal operation —
  // every specific failure mode we know about (WebAuthn origin
  // restrictions, a cancelled passphrase prompt, etc.) is already
  // caught and handled at its source. This exists so that anything NOT
  // yet anticipated fails visibly instead of leaving the identity panel
  // stuck at "generating…" forever with nothing shown.
  console.error('[station] boot failed:', err.stack || err.message)
  alert(`Station failed to start: ${err.message}\n\nTry reloading the page.`)
})
